import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

export interface UnifiedVideoPlayerProps {
  url: string;
  title?: string;
  autoPlay?: boolean;
  controls?: boolean;
  className?: string;
  poster?: string;
  /** Show native controls (simpler UI) */
  nativeControls?: boolean;
  /** For livestreams - hide progress bar and show live indicator */
  isLive?: boolean;
}

export function UnifiedVideoPlayer({
  url,
  title = 'Video',
  autoPlay = false,
  controls = true,
  className,
  poster,
  nativeControls = false,
  isLive = false,
}: UnifiedVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLiveStream, setIsLiveStream] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  // Initialize video and handle events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Set volume
    video.volume = volume;
    video.muted = muted;

    // Event handlers
    const handleTimeUpdate = () => {
      if (isFinite(video.duration) && video.duration > 0) {
        setProgress((video.currentTime / video.duration) * 100);
      }
    };

    const handleDurationChange = () => {
      if (isFinite(video.duration) && video.duration > 0 && video.duration !== Infinity) {
        setDuration(video.duration);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleWaiting = () => setIsBuffering(true);
    const handlePlaying = () => setIsBuffering(false);
    const handleEnded = () => setIsPlaying(false);
    const handleError = (e: Event) => {
      const target = e.target as HTMLMediaElement;
      if (target.error) {
        console.error('Video error:', target.error);
        setError('Reconnecting...');
        
        // Auto-retry with exponential backoff
        const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 30000); // Max 30s
        
        setTimeout(() => {
          const video = videoRef.current;
          if (video) {
            console.log(`Retry ${retryCount + 1}: Attempting to reconnect...`);
            video.load();
            if (isPlaying) {
              video.play().catch(() => {});
            }
            setRetryCount(prev => prev + 1);
          }
        }, retryDelay);
      }
    };

    const handleCanPlay = () => {
      setError(null);
      setIsBuffering(false);
      setRetryCount(0);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);

    // Sync initial playing state
    if (video.paused) {
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
    }

    // Auto-play if requested
    if (autoPlay) {
      const playVideo = () => {
        video.play().catch(() => {});
      };
      
      // Try immediately
      playVideo();
      
      // Also try when ready in case immediate play fails
      video.addEventListener('canplaythrough', playVideo, { once: true });
    }

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [url, autoPlay, isPlaying, retryCount]);

  // Toggle play/pause
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  };

  // Handle seek
  const handleSeek = (values: number[]) => {
    const video = videoRef.current;
    if (!video || !isFinite(video.duration) || video.duration <= 0) return;
    video.currentTime = (values[0] / 100) * video.duration;
    setProgress(values[0]);
  };

  // Format time
  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Calculate current time
  const currentTime = videoRef.current?.currentTime || 0;

  if (nativeControls) {
    // Simple native controls
    return (
      <div className={cn('relative w-full h-full', className)}>
        <video
          ref={videoRef}
          src={url}
          poster={poster}
          controls
          autoPlay={autoPlay}
          playsInline
          className="w-full h-full object-contain"
        />
      </div>
    );
  }

  // Custom controls
  return (
    <div className={cn('relative group w-full h-full overflow-hidden', className)}>
      <video
        ref={videoRef}
        src={url}
        poster={poster}
        className="w-full h-full object-contain"
        onClick={togglePlay}
        preload="auto"
        playsInline
        muted={muted}
      />

      {/* Loading/Buffering indicator */}
      {isBuffering && !isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10">
          <div className="w-12 h-12 rounded-full border-4 border-white/50 border-t-white animate-spin" />
        </div>
      )}

       {/* Error indicator - temporary reconnecting message */}
       {error && (
         <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
           <div className="flex items-center gap-2 bg-red-600/90 backdrop-blur-sm px-4 py-2 rounded-lg text-white text-sm shadow-lg">
             <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
             {error === 'Reconnecting...' ? 'Reconnecting...' : 'Error loading video'}
           </div>
         </div>
       )}

      {/* Live indicator */}
      {isLiveStream && (
        <div className="absolute top-4 left-4 z-20">
          <div className="flex items-center gap-2 bg-red-600/90 backdrop-blur-sm px-3 py-1.5 rounded-md text-white text-xs font-bold">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            LIVE
          </div>
        </div>
      )}

      {/* Play/Pause overlay when paused */}
      {!isPlaying && !isBuffering && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={togglePlay}
        >
          <div className="w-16 h-16 rounded-full bg-black/70 flex items-center justify-center backdrop-blur-sm hover:scale-110 transition-transform">
            <Play size={32} className="text-white ml-1" fill="white" />
          </div>
        </div>
      )}

      {/* Controls overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Progress bar (hide for live streams) */}
        {!isLiveStream && (
          <Slider
            value={[progress]}
            onValueChange={handleSeek}
            max={100}
            step={0.1}
            className="mb-3 [&_[role=slider]]:w-3 [&_[role=slider]]:h-3"
          />
        )}

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="text-white hover:text-primary transition-colors"
            >
              {isPlaying ? (
                <Pause size={24} fill="currentColor" />
              ) : (
                <Play size={24} fill="currentColor" className="ml-0.5" />
              )}
            </button>
            <span className="text-white text-sm">
              {formatTime(currentTime)} / {isFinite(duration) && duration > 0 ? formatTime(duration) : isLiveStream ? 'LIVE' : '--:--'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setMuted(!muted);
                const video = videoRef.current;
                if (video) video.muted = !muted;
              }}
              className="text-white hover:text-primary transition-colors"
            >
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
            <div className="w-20">
              <Slider
                value={[muted ? 0 : volume * 100]}
                onValueChange={(v) => {
                  setVolume(v[0] / 100);
                  setMuted(v[0] === 0);
                  const video = videoRef.current;
                  if (video) {
                    video.volume = v[0] / 100;
                    video.muted = v[0] === 0;
                  }
                }}
                max={100}
                step={1}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}